<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
});
const store = useStore();
const { t } = useI18n();

const useDefaultData = ref(true);
const viewMode = ref('grid');
const selectedCountry = ref('');
const selectedLanguage = ref('');
const isLoading = ref(false);
const isSaving = ref(false);

// Use product from props to sync use_default_data
const productData = computed(() => props.product || {});

// Modal state
const showModal = ref(false);
const editingItem = ref(null);
const formData = ref({
  folder_name: '',
  title: '',
  image_url: '',
  country: '',
  language: '',
});

const countryOptions = computed(() => [
  {
    value: '',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.ALL'),
  },
  {
    value: 'MY',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.MY'),
  },
  {
    value: 'ID',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.ID'),
  },
  {
    value: 'SG',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.SG'),
  },
  {
    value: 'TH',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.TH'),
  },
]);

const languageOptions = computed(() => [
  {
    value: '',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.ALL'),
  },
  {
    value: 'en',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.EN'),
  },
  {
    value: 'zh',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.ZH'),
  },
  {
    value: 'ms',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.MS'),
  },
  {
    value: 'th',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.TH'),
  },
]);

// Get certificates from store
const storeCertificates = computed(
  () => store.getters['aiCloser/getCertificates']
);

// Local certificates
const certificates = ref([]);

// Fetch certificates on mount
onMounted(async () => {
  if (!props.assistantId) return;
  // Use product from props
  if (props.product?.use_default_data !== undefined) {
    useDefaultData.value = props.product.use_default_data;
  }
  await handleRefresh();
});

// Sync use_default_data from product props
watch(
  () => props.product?.use_default_data,
  newVal => {
    if (newVal !== undefined) {
      useDefaultData.value = newVal;
    }
  },
  { immediate: true }
);

// Sync store to local
watch(
  () => storeCertificates.value,
  newVal => {
    certificates.value = (newVal || []).map(c => ({
      id: c.id,
      folder: c.folder_name || '',
      title: c.title || '',
      url: c.image_url || '',
      country: c.country || '',
      language: c.language || '',
      createdAt: c.created_at ? new Date(c.created_at).toLocaleString() : '',
      updatedAt: c.updated_at ? new Date(c.updated_at).toLocaleString() : '',
    }));
  },
  { immediate: true, deep: true }
);

const filteredCertificates = computed(() => {
  let items = certificates.value;
  if (selectedCountry.value) {
    items = items.filter(c => c.country === selectedCountry.value);
  }
  if (selectedLanguage.value) {
    items = items.filter(c => c.language === selectedLanguage.value);
  }
  return items;
});

const handleRefresh = async () => {
  if (!props.assistantId) return;
  isLoading.value = true;
  try {
    await store.dispatch('aiCloser/fetchCertificates', {
      assistantId: props.assistantId,
    });
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ALERTS.LOAD_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

// Modal handlers
const openAddModal = () => {
  editingItem.value = null;
  formData.value = {
    folder_name: '',
    title: '',
    image_url: '',
    country: '',
    language: '',
  };
  showModal.value = true;
};

const openEditModal = item => {
  editingItem.value = item;
  formData.value = {
    folder_name: item.folder,
    title: item.title,
    image_url: item.url,
    country: item.country,
    language: item.language,
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingItem.value = null;
};

const handleSave = async () => {
  if (!props.assistantId) return;
  if (!formData.value.title || !formData.value.image_url) {
    useAlert(
      t(
        'AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ALERTS.TITLE_IMAGE_URL_REQUIRED'
      )
    );
    return;
  }

  isSaving.value = true;
  try {
    if (editingItem.value) {
      await store.dispatch('aiCloser/updateCertificate', {
        assistantId: props.assistantId,
        certificateId: editingItem.value.id,
        data: formData.value,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ALERTS.UPDATED'));
    } else {
      await store.dispatch('aiCloser/createCertificate', {
        assistantId: props.assistantId,
        data: formData.value,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ALERTS.ADDED'));
    }
    closeModal();
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ALERTS.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async item => {
  if (
    !confirm(
      t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.DELETE_CONFIRM', {
        title: item.title,
      })
    )
  ) {
    return;
  }

  try {
    await store.dispatch('aiCloser/deleteCertificate', {
      assistantId: props.assistantId,
      certificateId: item.id,
    });
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ALERTS.DELETED'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ALERTS.DELETE_FAILED')
    );
  }
};

const isFormValid = computed(() => {
  return formData.value.title && formData.value.image_url;
});
</script>

<template>
  <div class="certificate-tab">
    <!-- Toggle Switch -->
    <div class="toggle-row">
      <button
        class="crt-switch"
        :class="{ 'is-on': useDefaultData }"
        type="button"
        @click="useDefaultData = !useDefaultData"
      >
        <span />
      </button>
      <span class="toggle-label">{{
        t('AILAB.ASSISTANT_V2.PRODUCT_INFO.TOGGLE.LABEL_DEFAULT')
      }}</span>
    </div>

    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <select v-model="selectedCountry" class="filter-select">
          <option
            v-for="opt in countryOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <select v-model="selectedLanguage" class="filter-select">
          <option
            v-for="opt in languageOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <button
          class="refresh-btn"
          :disabled="isLoading"
          @click="handleRefresh"
        >
          <span
            class="i-lucide-refresh-cw size-4"
            :class="[isLoading && 'animate-spin']"
          />
        </button>
      </div>
      <div class="toolbar-right">
        <button
          class="add-btn"
          :disabled="useDefaultData"
          @click="openAddModal"
        >
          <span class="i-lucide-plus size-4" />
          <span>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ACTIONS.ADD')
          }}</span>
        </button>
        <div class="view-toggle">
          <button
            class="view-btn"
            :class="[viewMode === 'list' && 'active']"
            @click="viewMode = 'list'"
          >
            <span class="i-lucide-list size-4" />
          </button>
          <button
            class="view-btn"
            :class="[viewMode === 'grid' && 'active']"
            @click="viewMode = 'grid'"
          >
            <span class="i-lucide-layout-grid size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Content Container -->
    <div class="content-container" :class="{ 'is-default': useDefaultData }">
      <!-- Default Watermark -->
      <div v-if="useDefaultData" class="default-watermark">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_INFO.DEFAULT_WATERMARK') }}
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="empty-state">
        <span class="i-lucide-loader-2 size-10 animate-spin text-orange-400" />
        <p class="empty-text">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.STATE.LOADING') }}
        </p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredCertificates.length === 0" class="empty-state">
        <div class="empty-icon">
          <span class="i-lucide-award size-8" />
        </div>
        <h4 class="empty-title">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.STATE.EMPTY_TITLE') }}
        </h4>
        <p class="empty-text">
          {{
            t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.STATE.EMPTY_SUBTITLE')
          }}
        </p>
        <button
          v-if="!useDefaultData"
          class="add-first-btn"
          @click="openAddModal"
        >
          <span class="i-lucide-plus size-4" />
          <span>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.ACTIONS.ADD_FIRST')
          }}</span>
        </button>
      </div>

      <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="certificate-grid">
        <div
          v-for="item in filteredCertificates"
          :key="item.id"
          class="certificate-card"
        >
          <div class="card-header">
            <div class="folder-info">
              <span class="i-lucide-award size-4" />
              <span class="folder-name">{{
                item.folder ||
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FOLDER.UNCATEGORIZED')
              }}</span>
            </div>
            <div v-if="!useDefaultData" class="card-actions">
              <button class="action-btn edit" @click="openEditModal(item)">
                <span class="i-lucide-pencil size-3.5" />
              </button>
              <button class="action-btn delete" @click="handleDelete(item)">
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
          <div class="card-content">
            <div class="media-preview">
              <img :src="item.url" :alt="item.title" class="preview-image" />
            </div>
            <div class="certificate-info">
              <h4 class="certificate-title">{{ item.title }}</h4>
              <div class="certificate-dates">
                <small>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.CREATED', {
                    date: item.createdAt,
                  })
                }}</small>
                <small>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.UPDATED', {
                    date: item.updatedAt,
                  })
                }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="certificate-list">
        <div
          v-for="item in filteredCertificates"
          :key="item.id"
          class="certificate-row"
        >
          <div class="media-thumb">
            <img :src="item.url" :alt="item.title" />
          </div>
          <div class="certificate-details">
            <div class="folder-info">
              <span class="i-lucide-award size-3.5" />
              <span>{{
                item.folder ||
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FOLDER.UNCATEGORIZED')
              }}</span>
            </div>
            <h4 class="certificate-title">{{ item.title }}</h4>
            <small class="certificate-date">{{
              t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.UPDATED', {
                date: item.updatedAt,
              })
            }}</small>
          </div>
          <div v-if="!useDefaultData" class="row-actions">
            <button class="action-btn edit" @click="openEditModal(item)">
              <span class="i-lucide-pencil size-4" />
            </button>
            <button class="action-btn delete" @click="handleDelete(item)">
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3>
              {{
                editingItem
                  ? t(
                      'AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.MODAL.EDIT_TITLE'
                    )
                  : t(
                      'AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.MODAL.ADD_TITLE'
                    )
              }}
            </h3>
            <button class="close-btn" @click="closeModal">
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label
                >{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.FORM.TITLE.LABEL')
                }}
                <span class="required">*</span></label
              >
              <input
                v-model="formData.title"
                type="text"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.FORM.TITLE.PLACEHOLDER'
                  )
                "
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label
                >{{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.FORM.IMAGE_URL.LABEL'
                  )
                }}
                <span class="required">*</span></label
              >
              <input
                v-model="formData.image_url"
                type="url"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.FORM.IMAGE_URL.PLACEHOLDER'
                  )
                "
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>{{
                t('AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.FORM.FOLDER.LABEL')
              }}</label>
              <input
                v-model="formData.folder_name"
                type="text"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_CERTIFICATE.FORM.FOLDER.PLACEHOLDER'
                  )
                "
                class="form-input"
              />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.COUNTRY.LABEL')
                }}</label>
                <select v-model="formData.country" class="form-input">
                  <option
                    v-for="opt in countryOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.LANGUAGE.LABEL')
                }}</label>
                <select v-model="formData.language" class="form-input">
                  <option
                    v-for="opt in languageOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </div>
            <!-- Preview -->
            <div v-if="formData.image_url" class="preview-section">
              <label>{{
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.PREVIEW.LABEL')
              }}</label>
              <div class="preview-box">
                <img
                  :src="formData.image_url"
                  :alt="
                    t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.PREVIEW.ALT')
                  "
                />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button
              class="btn-save"
              :disabled="!isFormValid || isSaving"
              @click="handleSave"
            >
              <span
                v-if="isSaving"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span>
                {{
                  editingItem
                    ? t('AILAB.FORM.UPDATE')
                    : t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MODAL.ACTIONS.ADD')
                }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.certificate-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 80px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--wlab-text-muted);
  border-radius: 24px;
  transition: 0.3s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider) {
  background: rgba(0, 102, 255, 0.25);
  border-color: rgba(0, 102, 255, 0.6);
}

:global(body.dark .toggle-switch input:checked + .toggle-slider) {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
  background: #ffffff;
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider::before) {
  background: #0066ff;
}

:global(body.dark .toggle-switch input:checked + .toggle-slider::before) {
  background: #ffffff;
}

.toggle-label {
  font-size: 14px;
  color: var(--wlab-text-secondary);
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  cursor: pointer;
}

.filter-select option {
  background: var(--wlab-bg-primary);
  color: var(--wlab-text-primary);
}

.refresh-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  color: var(--wlab-text-primary);
  cursor: pointer;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  color: #000000;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.view-toggle {
  display: flex;
  gap: 2px;
  background: var(--wlab-bg-tertiary);
  border-radius: 6px;
  padding: 2px;
}

.view-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.view-btn.active {
  background: rgba(255, 255, 255, 0.2);
  color: #0066ff;
}

/* Content Container */
.content-container {
  position: relative;
  padding: 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  min-height: 200px;
}

.content-container.is-default {
  opacity: 0.8;
}

.default-watermark {
  position: absolute;
  right: 3%;
  bottom: 5%;
  font-size: 5rem;
  font-weight: 700;
  color: var(--wlab-border-light);
  pointer-events: none;
  z-index: 1;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  min-height: 200px;
}

.empty-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(0, 102, 255, 0.15) 0%,
    rgba(0, 102, 255, 0.05) 100%
  );
  border: 1px solid rgba(0, 102, 255, 0.2);
  color: #0066ff;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--wlab-text-primary);
  margin: 0 0 8px 0;
}

.empty-text {
  font-size: 14px;
  color: var(--wlab-text-muted);
  margin: 0;
}

.add-first-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  margin-top: 16px;
  background: #ffffff;
  border: none;
  border-radius: 8px;
  color: #000000;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-first-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

/* Grid View */
.certificate-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  position: relative;
  z-index: 2;
}

.certificate-card {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s;
}

.certificate-card:hover {
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.card-header {
  padding: 12px;
  border-bottom: 1px solid var(--wlab-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.folder-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--wlab-text-secondary);
}

.folder-name {
  font-size: 13px;
  font-weight: 600;
}

.card-actions,
.row-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.edit {
  color: var(--wlab-text-secondary);
}

.action-btn.edit:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.action-btn.delete {
  color: var(--wlab-text-muted);
}

.action-btn.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}

.card-content {
  padding: 12px;
  display: flex;
  gap: 12px;
}

.media-preview {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.certificate-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.certificate-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--wlab-text-primary);
  margin: 0;
}

.certificate-dates {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
}

.certificate-dates small {
  font-size: 11px;
  color: var(--wlab-text-muted);
}

/* List View */
.certificate-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 2;
}

.certificate-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 8px;
  transition: all 0.2s;
}

.certificate-row:hover {
  background: var(--wlab-border-light);
}

.media-thumb {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.media-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.certificate-details {
  flex: 1;
  min-width: 0;
}

.certificate-details .folder-info {
  font-size: 12px;
  margin-bottom: 4px;
}

.certificate-details .certificate-title {
  font-size: 14px;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.certificate-date {
  font-size: 11px;
  color: var(--wlab-text-muted);
}

.row-actions {
  flex-shrink: 0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: var(--wlab-bg-primary, #1a1a1a);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--wlab-border-light);
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--wlab-text-primary);
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--wlab-text-primary);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
}

.required {
  color: #ef4444;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-input {
  padding: 10px 12px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input, #2a2a2a);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #ffffff;
}

.form-input::placeholder {
  color: var(--wlab-text-muted);
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-section label {
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
}

.preview-box {
  width: 100%;
  max-height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
}

.preview-box img {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--wlab-border-light);
}

.btn-cancel {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.05);
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
